import { SpeedRounded } from '@mui/icons-material'
import { Box, Button, ButtonGroup, Stack, Typography, useTheme } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { useState } from 'react'
import CostUtilizationView from '../contract_analytics/CostUtilizationView'
import ExpensesByTosView from '../contract_analytics/ExpensesByTosView'
import NormalView from '../contract_analytics/NormalView'
import { snackbar } from '../services/SnackbarService'

interface P {
    contractId: number
    visualizedFilterDate: string
    filterStartDate: Date
    filterEndDate: Date
    updateDataFlag: boolean
    handleFilterDateChange: (newValue: moment.Moment | null) => void
    handleCsvGeneration: (fileType: string, source?: string) => void
    setExportMenu: (items: string[]) => void
    viewMode?: string
    setViewMode?: (name: string) => void
}

export type ViewType = 'normal' | 'expenseByTos' | 'costUtilization'

export default function NewContractAnalytics({ contractId, visualizedFilterDate, filterStartDate, filterEndDate, updateDataFlag, handleFilterDateChange, handleCsvGeneration, setExportMenu, viewMode, setViewMode }: P) {
    const theme = useTheme()
    const [activeView, setActiveView] = useState<ViewType>('normal')

    const underDev = () => {
        snackbar.show({
            severity: 'info',
            text: 'This functionality will be developed in future.'
        })
    }

    const currentView = (view: ViewType) => {
        switch (view) {
            case 'normal':
                return (
                    <NormalView
                        contractId={contractId}
                        changeViewFunction={setActiveView}
                        filterEndDate={filterEndDate.toISOString().slice(0, 7)}
                        filterStartDate={filterStartDate.toISOString().slice(0, 7)}
                        displayUnderDev={underDev}
                        updateDataFlag={updateDataFlag}
                        handleCsvGeneration={handleCsvGeneration}
                        setExportMenu={setExportMenu}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                    />
                )
            case 'expenseByTos':
                return (
                    <ExpensesByTosView
                        contractId={contractId}
                        changeViewFunction={setActiveView}
                        filterEndDate={filterEndDate.toISOString().slice(0, 7)}
                        filterStartDate={filterStartDate.toISOString().slice(0, 7)}
                        updateDataFlag={updateDataFlag}
                        setExportMenu={setExportMenu}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                    />
                )
            case 'costUtilization':
                return (
                    <CostUtilizationView
                        contractId={contractId}
                        changeViewFunction={setActiveView}
                        filterEndDate={filterEndDate.toISOString().slice(0, 7)}
                        filterStartDate={filterStartDate.toISOString().slice(0, 7)}
                        updateDataFlag={updateDataFlag}
                        setExportMenu={setExportMenu}
                    />
                )
        }
    }

    return (
        <Stack sx={{ height: '100%' }}>
            <Box>
                <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                    <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                        <ButtonGroup>
                            <Button variant='contained' color='info' sx={{ textTransform: 'capitalize' }} onClick={underDev}>Performance</Button>
                            <Button variant='outlined' sx={{ textTransform: 'capitalize' }} onClick={underDev}>Operations</Button>
                        </ButtonGroup>
                        <Button variant='text' sx={{ textTransform: 'capitalize' }} startIcon={<SpeedRounded />} onClick={underDev}>Quality performance</Button>
                    </Stack>
                    <ButtonGroup
                        sx={{
                            'display': 'flex',
                            'alignItems': 'stretch',
                            '& .MuiButtonGroup-grouped': {
                                minWidth: 'fit-content'
                            }
                        }}>

                        <Box sx={{
                            display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '20px 0 0 20px !important', border: '1px solid', px: 2
                        }}>
                            <Typography variant='button' color={theme.palette.text.secondary} sx={{ textTransform: 'capitalize' }}>{visualizedFilterDate}</Typography>
                        </Box>

                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                views={['month', 'year']}
                                onAccept={handleFilterDateChange}
                                value={moment(filterEndDate.toISOString(), 'YYYY-MM')}
                                maxDate={moment()}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            'fontSize': '0.875rem',
                                            '& .MuiOutlinedInput-root': {
                                                'borderRadius': '0 20px 20px 0 !important',
                                                '& fieldset': {
                                                    border: '1px solid',
                                                    borderLeft: 'none'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderWidth: '1px'
                                                }
                                            },
                                            '& .MuiInputBase-input': {
                                                padding: '8px 12px',
                                                minWidth: '120px',
                                                width: 'auto'
                                            },
                                            '& .MuiInputAdornment-root': {
                                                marginLeft: 0
                                            }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </ButtonGroup>
                </Stack>
                {currentView(activeView)}
            </Box>
        </Stack>
    )
}
